import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Copy,
  ExternalLink,
  Trophy,
  Sparkles,
  Tv,
  PartyPopper,
  Lightbulb,
  Building2,
  Globe,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useCreateVenue } from "@/hooks/use-venue-mutations";
import { useCreateLeaderboard } from "@/hooks/use-leaderboard-mutations";
import { apiGet } from "@/lib/api-client";
import { useCreateCheckoutSession } from "@/hooks/use-subscription";
import { useToast } from "@/hooks/use-toast";

import membershipUpsellImg from "@/assets/templates/membership-upsell.jpg";
import loyaltyConversionImg from "@/assets/templates/loyalty-conversion.jpg";
import merchSalesImg from "@/assets/templates/merch-sales.jpg";
import generalCompetitionImg from "@/assets/templates/general-competition.jpg";
import guestExperienceImg from "@/assets/templates/guest-experience.jpg";
import upsellSprintImg from "@/assets/templates/upsell-sprint.jpg";

const TOTAL_STEPS = 5;

interface TemplateOption {
  id: string;
  name: string;
  description: string;
  category: string;
  image: string;
  popular?: boolean;
  config: {
    name: string;
    metric: "memberships" | "value";
    topN: number;
    selectedProducts: string[];
    prize1: string;
    prize2: string;
    prize3: string;
    managerMsg: string;
  };
}

interface VenueEnrichment {
  name: string | null;
  description: string | null;
  primaryColor: string | null;
  logoUrl: string | null;
}

function getTemplates(venueName: string): TemplateOption[] {
  const n = venueName || "Your Venue";
  return [
    {
      id: "membership-upsell",
      name: `${n} Membership Blitz`,
      description: `Drive ${n} membership sales with a head-to-head staff leaderboard.`,
      category: "Membership",
      image: membershipUpsellImg,
      popular: true,
      config: {
        name: `${n} Membership Blitz`,
        metric: "memberships",
        topN: 10,
        selectedProducts: ["p1", "p2", "p3", "p4"],
        prize1: "$1,000 Gift Card",
        prize2: "$500 Gift Card",
        prize3: "$250 Gift Card",
        managerMsg: `Every guest at ${n} is a potential member — let's convert! 🚀`,
      },
    },
    {
      id: "loyalty-conversion",
      name: `${n} Loyalty Drive`,
      description: `Convert ${n} guests into your loyalty program and build repeat visits.`,
      category: "Loyalty",
      image: loyaltyConversionImg,
      popular: true,
      config: {
        name: `${n} Loyalty Drive`,
        metric: "memberships",
        topN: 10,
        selectedProducts: ["p1", "p2"],
        prize1: "Extra Day Off + $500",
        prize2: "$300 Gift Card",
        prize3: "$150 Gift Card",
        managerMsg: `${n} loyalty members visit 3x more often — every sign-up pays off for months! 💪`,
      },
    },
    {
      id: "merch-sales",
      name: `${n} Merch Sprint`,
      description: `Boost ${n} retail and merchandise revenue by gamifying product sales.`,
      category: "Product Sales",
      image: merchSalesImg,
      config: {
        name: `${n} Merch Sprint`,
        metric: "value",
        topN: 10,
        selectedProducts: ["p7", "p8"],
        prize1: "$750 Bonus",
        prize2: "$400 Bonus",
        prize3: "$200 Bonus",
        managerMsg: `${n} merch margins are huge — every upsell counts! 🧦🎉`,
      },
    },
    {
      id: "general-competition",
      name: `${n} Team Challenge`,
      description: `Flexible, all-purpose competition for your ${n} team.`,
      category: "Competition",
      image: generalCompetitionImg,
      config: {
        name: `${n} Team Challenge`,
        metric: "memberships",
        topN: 10,
        selectedProducts: ["p1", "p2", "p3", "p4", "p5", "p6"],
        prize1: "Winner's Trophy + $500",
        prize2: "$250 Gift Card",
        prize3: "$100 Gift Card",
        managerMsg: `${n} team — may the best performer win! 🏆`,
      },
    },
    {
      id: "guest-experience",
      name: `${n} Guest Experience`,
      description: `Rank ${n} staff by customer feedback scores.`,
      category: "Feedback",
      image: guestExperienceImg,
      config: {
        name: `${n} Guest Experience`,
        metric: "value",
        topN: 10,
        selectedProducts: [],
        prize1: "Employee of the Month + $500",
        prize2: "$250 Gift Card",
        prize3: "Free Team Lunch",
        managerMsg: `Our ${n} guests are everything — who's delivering world-class experiences? ⭐`,
      },
    },
    {
      id: "upsell-sprint",
      name: `${n} Weekend Sprint`,
      description: `Short, intense weekend competitions at ${n}. High energy, quick wins.`,
      category: "Upsell",
      image: upsellSprintImg,
      config: {
        name: `${n} Weekend Sprint`,
        metric: "value",
        topN: 8,
        selectedProducts: ["p5", "p6", "p7", "p8"],
        prize1: "$300 Cash Bonus",
        prize2: "$150 Cash Bonus",
        prize3: "$75 Cash Bonus",
        managerMsg: `48 hours at ${n}. One leaderboard. Let's smash last weekend's record! ⚡`,
      },
    },
  ];
}

interface OnboardingWizardProps {
  onComplete: (venueId: string) => void;
}

const OnboardingWizard = ({ onComplete }: OnboardingWizardProps) => {
  const [step, setStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateOption | null>(null);
  const [venueId, setVenueId] = useState<string | null>(null);
  const [boardId, setBoardId] = useState<string | null>(null);
  const [venueName, setVenueName] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [venueInfo, setVenueInfo] = useState<VenueEnrichment | null>(null);
  const [isEnriching, setIsEnriching] = useState(false);
  const [config, setConfig] = useState({
    name: "",
    startDate: "",
    endDate: "",
    metric: "memberships" as "memberships" | "value",
    topN: 10,
    selectedProducts: [] as string[],
    prize1: "",
    prize2: "",
    prize3: "",
    managerMsg: "",
  });
  const [copied, setCopied] = useState(false);
  const [venueError, setVenueError] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);

  const { toast } = useToast();
  const createVenueMutation = useCreateVenue();
  const createLeaderboardMutation = useCreateLeaderboard(venueId);
  const checkoutMutation = useCreateCheckoutSession();

  const displayUrl = boardId
    ? `${window.location.origin}/display/${boardId}`
    : `${window.location.origin}/display/my-first-campaign`;

  const handleStartOnboarding = async () => {
    setVenueError(null);
    try {
      let resolvedName = venueName.trim() || "My Venue";

      if (websiteUrl.trim()) {
        setIsEnriching(true);
        try {
          const res = await apiGet<{ success: boolean; data: VenueEnrichment }>(
            `/venues/enrich?url=${encodeURIComponent(websiteUrl.trim())}`
          );
          if (res.success && res.data) {
            setVenueInfo(res.data);
            if (res.data.name) {
              resolvedName = res.data.name;
              setVenueName(res.data.name);
            }
          }
        } catch {
          // non-fatal — proceed with user-entered name
        } finally {
          setIsEnriching(false);
        }
      }

      const baseSlug = resolvedName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      const slug = `${baseSlug}-${Math.random().toString(36).slice(2, 7)}`;
      const newVenue = await createVenueMutation.mutateAsync({
        name: resolvedName,
        slug,
        description: `Leaderboard venue for ${resolvedName}`,
      });
      setVenueId(newVenue.id);
      setStep(2);
    } catch (error) {
      console.error("Failed to create venue:", error);
      setVenueError("Failed to set up your account. Please try again.");
    }
  };

  const handleSelectTemplate = (t: TemplateOption) => {
    setSelectedTemplate(t);
    setConfig({
      ...config,
      name: t.config.name,
      metric: t.config.metric,
      topN: t.config.topN,
      selectedProducts: t.config.selectedProducts,
      prize1: t.config.prize1,
      prize2: t.config.prize2,
      prize3: t.config.prize3,
      managerMsg: t.config.managerMsg,
      startDate: "2026-03-01",
      endDate: "2026-03-31",
    });
    setStep(3);
  };

  const handlePublish = async () => {
    if (!venueId) {
      console.error("No venue ID available");
      return;
    }
    setPublishError(null);
    try {
      const newBoard = await createLeaderboardMutation.mutateAsync({
        name: config.name,
        description: config.managerMsg,
        period: "monthly",
      });
      setBoardId(newBoard.id);
      setStep(5);
    } catch (error) {
      console.error("Failed to create leaderboard:", error);
      setPublishError("Failed to publish your campaign. Please try again.");
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(displayUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isBusy = createVenueMutation.isPending || isEnriching;

  return (
    <div className="mx-auto max-w-3xl py-8">
      {/* Progress */}
      <div className="mb-10 flex items-center justify-center gap-2">
        {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold transition-colors ${
                step > s
                  ? "bg-accent text-accent-foreground"
                  : step === s
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {step > s ? <Check className="h-4 w-4" /> : s}
            </div>
            {s < TOTAL_STEPS && (
              <div className={`h-0.5 w-10 ${step > s ? "bg-accent" : "bg-muted"}`} />
            )}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* Step 1: Welcome */}
        {step === 1 && (
          <StepWrapper stepKey="welcome">
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.2 }}
                className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-primary/10"
              >
                <Sparkles className="h-10 w-10 text-primary" />
              </motion.div>
              <h1 className="text-3xl font-extrabold">Welcome to Leaderboards! 🎉</h1>
              <p className="mx-auto mt-3 max-w-lg text-muted-foreground">
                Let's create your first campaign in about 2 minutes. We'll pick a template,
                customise the details, and get your leaderboard live on a TV screen.
              </p>

              <div className="mx-auto mt-8 max-w-md space-y-3 text-left">
                <div className="space-y-1.5">
                  <Label htmlFor="venue-name">What's your venue called?</Label>
                  <Input
                    id="venue-name"
                    value={venueName}
                    onChange={(e) => setVenueName(e.target.value)}
                    placeholder="e.g. City Trampoline Park, Fun Zone, Adventure World"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="venue-website" className="flex items-center gap-1.5">
                    <Globe className="h-3.5 w-3.5" /> Website
                    <span className="text-muted-foreground font-normal">
                      (optional — we'll pull your branding)
                    </span>
                  </Label>
                  <Input
                    id="venue-website"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    placeholder="https://yourvenue.com"
                  />
                </div>
              </div>

              <div className="mx-auto mt-6 max-w-md space-y-3">
                <Tip icon={<Trophy className="h-4 w-4" />} text="Leaderboards gamify sales so your team competes in real-time" />
                <Tip icon={<Tv className="h-4 w-4" />} text="Display results on any TV with a simple URL — no app install needed" />
                <Tip icon={<Lightbulb className="h-4 w-4" />} text="Venues using leaderboards see 20-40% lift in target product sales" />
              </div>

              <Button
                size="lg"
                className="mt-10"
                onClick={handleStartOnboarding}
                disabled={isBusy || !venueName.trim()}
              >
                {isEnriching ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                      className="mr-2 h-4 w-4 rounded-full border-2 border-primary-foreground border-t-transparent"
                    />
                    Fetching branding…
                  </>
                ) : createVenueMutation.isPending ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                      className="mr-2 h-4 w-4 rounded-full border-2 border-primary-foreground border-t-transparent"
                    />
                    Creating…
                  </>
                ) : (
                  <>
                    Let's go <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
              {venueError && (
                <p className="mt-3 text-sm text-destructive">{venueError}</p>
              )}
            </div>
          </StepWrapper>
        )}

        {/* Step 2: Pick Template */}
        {step === 2 && (
          <StepWrapper stepKey="template">
            <div className="text-center mb-8">
              <div className="mb-4 flex justify-center">
                {venueInfo?.logoUrl ? (
                  <img
                    src={venueInfo.logoUrl}
                    alt={venueName}
                    className="h-10 w-auto max-w-[160px] object-contain"
                  />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                )}
              </div>
              <h2 className="text-2xl font-bold">What do you want to drive at {venueName}?</h2>
              <p className="mt-1 text-muted-foreground">
                We've customised these templates for {venueName} — pick one and we'll pre-fill everything.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {getTemplates(venueName).map((t, i) => (
                <motion.button
                  key={t.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                  onClick={() => handleSelectTemplate(t)}
                  className="group overflow-hidden rounded-xl border bg-card text-left transition-all hover:border-primary hover:shadow-lg hover:shadow-primary/10"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={t.image}
                      alt={t.name}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    {t.popular && (
                      <Badge className="absolute right-2 top-2 bg-neon-orange text-white border-0 text-[10px]">
                        Popular
                      </Badge>
                    )}
                    {venueInfo?.logoUrl ? (
                      <img
                        src={venueInfo.logoUrl}
                        alt={venueName}
                        className="absolute left-2 top-2 h-6 w-auto max-w-[80px] object-contain opacity-80"
                      />
                    ) : (
                      <div className="absolute left-2 top-2 flex h-6 w-6 items-center justify-center rounded bg-white/20 backdrop-blur-sm">
                        <Building2 className="h-3.5 w-3.5 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-sm">{t.name}</h3>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{t.description}</p>
                  </div>
                </motion.button>
              ))}
            </div>
            <div className="mt-6 text-center">
              <button onClick={() => setStep(1)} className="text-sm text-muted-foreground hover:underline">
                <ArrowLeft className="mr-1 inline h-3 w-3" /> Back
              </button>
            </div>
          </StepWrapper>
        )}

        {/* Step 3: Customise */}
        {step === 3 && (
          <StepWrapper stepKey="customise">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold">Customise your campaign</h2>
              <p className="mt-1 text-muted-foreground">
                We've pre-filled everything from the <strong>{selectedTemplate?.name}</strong> template.
                Tweak anything you like.
              </p>
            </div>

            <div className="space-y-6">
              {/* Name & Dates */}
              <div className="rounded-xl border bg-card p-5 space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">1</span>
                  Campaign Details
                </h3>
                <div className="space-y-1.5">
                  <Label>Campaign Name</Label>
                  <Input
                    value={config.name}
                    onChange={(e) => setConfig({ ...config, name: e.target.value })}
                    placeholder="e.g. March Membership Madness"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Start Date</Label>
                    <Input type="date" value={config.startDate} onChange={(e) => setConfig({ ...config, startDate: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>End Date</Label>
                    <Input type="date" value={config.endDate} onChange={(e) => setConfig({ ...config, endDate: e.target.value })} />
                  </div>
                </div>
              </div>

              {/* Products */}
              <div className="rounded-xl border bg-card p-5 space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">2</span>
                  Which products count?
                </h3>
                <p className="text-xs text-muted-foreground">
                  Sales of these products will be tracked on the leaderboard.
                </p>
                <div className="flex items-center gap-2 rounded-lg bg-primary/5 border border-primary/10 px-3 py-2">
                  <svg viewBox="0 0 24 24" className="h-4 w-4 text-primary flex-shrink-0" fill="currentColor" stroke="currentColor" strokeWidth="0">
                    <path d="M8 4l8 8-8 8" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="text-xs font-medium text-primary">Synced from ROLLER</span>
                  <span className="text-xs text-muted-foreground">· Last synced 1 minute ago</span>
                  <span className="ml-auto h-2 w-2 rounded-full bg-accent animate-pulse" />
                </div>
                {/* Products section - ROLLER sync placeholder */}
                <div className="rounded-lg bg-primary/5 border border-primary/10 px-3 py-2 text-xs text-muted-foreground">
                  <p className="font-medium text-primary">Products will sync from your ROLLER account</p>
                  <p className="mt-1">Once your venue is connected, you can select which products count toward this leaderboard.</p>
                </div>
              </div>

              {/* Rewards */}
              <div className="rounded-xl border bg-card p-5 space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">3</span>
                  Rewards
                </h3>
                <p className="text-xs text-muted-foreground">
                  What's up for grabs? Rewards are displayed on the TV to keep motivation high.
                </p>
                <div className="grid gap-3">
                  <div className="space-y-1.5">
                    <Label>🥇 1st Place</Label>
                    <Input value={config.prize1} onChange={(e) => setConfig({ ...config, prize1: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>🥈 2nd Place</Label>
                    <Input value={config.prize2} onChange={(e) => setConfig({ ...config, prize2: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>🥉 3rd Place</Label>
                    <Input value={config.prize3} onChange={(e) => setConfig({ ...config, prize3: e.target.value })} />
                  </div>
                </div>
              </div>

              {/* Manager Message */}
              <div className="rounded-xl border bg-card p-5 space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">4</span>
                  Manager Message
                </h3>
                <p className="text-xs text-muted-foreground">
                  This message rotates on the TV display — a personal touch from the boss goes a long way.
                </p>
                <textarea
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  rows={3}
                  value={config.managerMsg}
                  onChange={(e) => setConfig({ ...config, managerMsg: e.target.value })}
                />
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between">
              <button onClick={() => setStep(2)} className="text-sm text-muted-foreground hover:underline">
                <ArrowLeft className="mr-1 inline h-3 w-3" /> Change template
              </button>
              <Button size="lg" onClick={() => setStep(4)} disabled={!config.name.trim()}>
                Preview <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </StepWrapper>
        )}

        {/* Step 4: Preview & Publish */}
        {step === 4 && (
          <StepWrapper stepKey="preview">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold">Looking good! 👀</h2>
              <p className="mt-1 text-muted-foreground">
                Here's what your team will see on the big screen. Ready to go live?
              </p>
            </div>

            {/* Mini preview */}
            <div className="rounded-xl border bg-card shadow-lg overflow-hidden">
              <div className="tv-gradient p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-tv-text">{config.name}</h3>
                  <span className="text-sm text-neon-cyan animate-pulse-neon">30d remaining</span>
                </div>
                <div className="space-y-2">
                  {/* Static preview - shows what the leaderboard will look like */}
                  {[
                    { id: "p1", name: "Team Member 1", membershipsSold: 24, valueSold: 2160 },
                    { id: "p2", name: "Team Member 2", membershipsSold: 19, valueSold: 1710 },
                    { id: "p3", name: "Team Member 3", membershipsSold: 15, valueSold: 1350 },
                    { id: "p4", name: "Team Member 4", membershipsSold: 12, valueSold: 1080 },
                    { id: "p5", name: "Team Member 5", membershipsSold: 8, valueSold: 720 },
                  ].map((s, i) => (
                    <div key={s.id} className={`flex items-center gap-3 rounded-lg px-4 py-2.5 ${
                      i === 0 ? "bg-gradient-to-r from-neon-orange/20 to-transparent border border-neon-orange/30" : "bg-tv-surface"
                    }`}>
                      <span className={`w-6 text-center text-lg font-extrabold ${i === 0 ? "text-neon-orange" : i < 3 ? "text-tv-text" : "text-tv-muted"}`}>
                        {i + 1}
                      </span>
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-tv-surface/80 text-xs font-bold text-tv-text">
                        {i + 1}
                      </div>
                      <span className="flex-1 text-sm font-semibold text-tv-text">{s.name}</span>
                      <span className="text-sm font-bold text-neon-orange">{s.membershipsSold} sold</span>
                      <span className="text-xs text-tv-muted">${s.valueSold.toLocaleString()}</span>
                    </div>
                  ))}
                  <p className="text-center text-xs text-tv-muted pt-2">+ {Math.max(0, config.topN - 5)} more staff</p>
                </div>
              </div>
              <div className="border-t p-4">
                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                  <span className="rounded-full bg-muted px-2.5 py-1">🏆 {config.prize1}</span>
                  <span className="rounded-full bg-muted px-2.5 py-1">📊 Tracking: {config.metric === "memberships" ? "# sold" : "$ value"}</span>
                  <span className="rounded-full bg-muted px-2.5 py-1">📅 {config.startDate} → {config.endDate}</span>
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between">
              <button onClick={() => setStep(3)} className="text-sm text-muted-foreground hover:underline">
                <ArrowLeft className="mr-1 inline h-3 w-3" /> Back to edit
              </button>
              <Button size="lg" onClick={handlePublish} disabled={createLeaderboardMutation.isPending}>
                {createLeaderboardMutation.isPending ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                      className="mr-2 h-4 w-4 rounded-full border-2 border-primary-foreground border-t-transparent"
                    />
                    Publishing…
                  </>
                ) : (
                  <>
                    Publish Campaign <Sparkles className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
              {publishError && (
                <p className="mt-3 text-sm text-destructive">{publishError}</p>
              )}
            </div>
          </StepWrapper>
        )}

        {/* Step 5: Launch Celebration */}
        {step === 5 && (
          <StepWrapper stepKey="launch">
            <div className="text-center">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", damping: 10 }}
                className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-accent/10"
              >
                <PartyPopper className="h-10 w-10 text-accent" />
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-3xl font-extrabold"
              >
                You're live! 🚀
              </motion.h1>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mx-auto mt-3 max-w-md text-muted-foreground"
              >
                <strong>{config.name}</strong> is now running. Grab this link, open it on a TV in the
                break room, and brief the team at your next ops meeting. Let the games begin!
              </motion.p>

              {/* Display URL */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="mx-auto mt-8 max-w-md rounded-xl border bg-card p-5"
              >
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  📺 Your TV Display URL
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 truncate rounded-lg bg-muted px-3 py-2 text-xs">{displayUrl}</code>
                  <Button size="sm" variant="outline" onClick={handleCopy}>
                    {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  </Button>
                  <Button size="sm" onClick={() => window.open(displayUrl, "_blank")}>
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                </div>
              </motion.div>

              {/* Tips */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
                className="mx-auto mt-8 max-w-md space-y-2 text-left"
              >
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  💡 Pro tips
                </p>
                <Tip icon={<Tv className="h-4 w-4" />} text="Open the URL on any browser — smart TVs, Chromecast, Fire Stick all work" />
                <Tip icon={<Trophy className="h-4 w-4" />} text="Announce the prizes at your next team huddle for maximum buy-in" />
                <Tip icon={<Lightbulb className="h-4 w-4" />} text="The leaderboard updates in real-time as sales flow in from ROLLER" />
              </motion.div>


              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.1 }}
              >
                <Button
                  size="lg"
                  className="mt-10"
                  onClick={async () => {
                    if (!venueId) {
                      toast({
                        title: "Error",
                        description: "Unable to find venue ID. Please try again.",
                      });
                      return;
                    }
                    try {
                      await checkoutMutation.mutateAsync({ venueId });
                      onComplete(venueId);
                    } catch (error) {
                      toast({
                        title: "Checkout Error",
                        description: "Failed to create checkout session. Please try again.",
                      });
                    }
                  }}
                  disabled={checkoutMutation.isPending}
                >
                  {checkoutMutation.isPending ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                        className="mr-2 h-4 w-4 rounded-full border-2 border-primary-foreground border-t-transparent"
                      />
                      Starting Free Trial…
                    </>
                  ) : (
                    <>
                      Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
                <p className="mt-4 text-xs text-muted-foreground">
                  7-day free trial • $40/mo after trial • Cancel anytime
                </p>
              </motion.div>
            </div>
          </StepWrapper>
        )}
      </AnimatePresence>
    </div>
  );
};

const StepWrapper = ({ stepKey, children }: { stepKey: string; children: React.ReactNode }) => (
  <motion.div
    key={stepKey}
    initial={{ opacity: 0, x: 40 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -40 }}
    transition={{ duration: 0.3 }}
  >
    {children}
  </motion.div>
);

const Tip = ({ icon, text }: { icon: React.ReactNode; text: string }) => (
  <div className="flex items-start gap-3 rounded-lg bg-muted/50 p-3">
    <span className="mt-0.5 text-primary">{icon}</span>
    <p className="text-sm text-muted-foreground">{text}</p>
  </div>
);

export default OnboardingWizard;
